import { io, type Socket } from 'socket.io-client';
import type { AIEvent } from '@elmes/shared';
import { logger } from '../lib/logger.js';

// Shared singleton WebSocket connection for system-wide AI events.
// Backend bridges all eventBus events to the `admin` room as `ai:event`
// with the AIEvent envelope (see packages/api/src/config/websocket.ts:603-611).
//
// Components subscribe through `useRealtimeEvent` (typed by event.type).
// useSocket / useNotificationSocket each maintain their own connections
// today; consolidating is a separate roadmap item.

let socket: Socket | null = null;
let socketToken: string | null = null;

type RealtimeHandler = (payload: Record<string, unknown>, event: AIEvent) => void;

const handlers = new Map<string, Set<RealtimeHandler>>();

/**
 * RAW SOCKET EVENT PLUMBING (gh#2059 SRS G7-A PR-6) — the second thing this singleton
 * carries, and the reason it is here rather than in a new module.
 *
 * ── WHY NOT A THIRD SOCKET ────────────────────────────────────────────────
 * The dm item feed needs two things the `ai:event` bridge above cannot give it: an
 * event name of its own (`dm:item:event`, delivered to a ROOM rather than broadcast to
 * `admin`) and the ability to SEND — a subscription with an ack. The obvious shape is a
 * dedicated `io()` connection, and it is the wrong one. This bundle already opens two
 * (`useSocket`, `useNotificationSocket` — see the header) and a third would mean a third
 * handshake, a third JWT lifetime to reason about and a third thing to reconnect after a
 * network blip, in exchange for capabilities the existing connection has.
 *
 * ── THE REGISTRY IS THE SOCKET'S, NOT THE HANDLER'S ───────────────────────
 * Subscribers register a NAME here, and exactly ONE bridge listener per name is attached
 * to whatever socket is current. That indirection is what makes a token rotation
 * survivable: `getRealtimeSocket` replaces the socket object, and re-attaching one
 * bridge per live name is the whole of the migration — while a caller that had done
 * `socket.on(...)` itself would be listening to a socket nobody holds any more, silently,
 * with its unsubscribe closure pointing at the same dead object.
 */
type SocketEventHandler = (...args: unknown[]) => void;

/** name → subscribers. A name with an empty set is removed, never left behind. */
const socketEventHandlers = new Map<string, Set<SocketEventHandler>>();

/** name → the ONE listener actually attached to the socket for that name. */
const socketEventBridges = new Map<string, SocketEventHandler>();

function bridgeFor(name: string): SocketEventHandler {
  const existing = socketEventBridges.get(name);
  if (existing) return existing;
  const bridge: SocketEventHandler = (...args: unknown[]) => {
    const set = socketEventHandlers.get(name);
    if (!set || set.size === 0) return;
    // A COPY, so a handler that unsubscribes itself (the terminal-disconnect arm does)
    // cannot mutate the set being iterated.
    for (const handler of [...set]) {
      try {
        handler(...args);
      } catch (err) {
        // One bad subscriber must not kill the others — the `dispatch` rule above.
        logger.error({ err, socketEvent: name }, '[realtimeBus] socket handler threw');
      }
    }
  };
  socketEventBridges.set(name, bridge);
  return bridge;
}

/** Move every live bridge onto `target`. Called when a socket is (re)created. */
function attachBridges(target: Socket): void {
  for (const [name, bridge] of socketEventBridges) target.on(name, bridge);
}

/** Take every live bridge off `target`. Called before a socket is discarded. */
function detachBridges(target: Socket): void {
  for (const [name, bridge] of socketEventBridges) target.off(name, bridge);
}

function dispatch(event: AIEvent): void {
  const set = handlers.get(event.type);
  if (!set || set.size === 0) return;
  for (const handler of set) {
    try {
      handler(event.payload, event);
    } catch (err) {
      // One bad subscriber must not kill the others.
      logger.error({ err, eventType: event.type }, '[realtimeBus] handler threw');
    }
  }
}

export function getRealtimeSocket(token: string): Socket {
  if (socket && socketToken === token) return socket;

  if (socket) {
    socket.off('ai:event', dispatch);
    // The raw-event bridges move to the NEW socket below; the subscriber registry is
    // untouched, exactly as `handlers` is. A token rotation must not silently unsubscribe
    // every mounted component.
    detachBridges(socket);
    socket.disconnect();
    socket = null;
  }

  socketToken = token;
  socket = io(window.location.origin, {
    path: '/api/socket.io',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionAttempts: 10,
  });
  socket.on('ai:event', dispatch);
  attachBridges(socket);
  return socket;
}

export function disconnectRealtimeSocket(): void {
  if (socket) {
    socket.off('ai:event', dispatch);
    detachBridges(socket);
    socket.disconnect();
    socket = null;
    socketToken = null;
  }
  handlers.clear();
  // CLEARED, exactly as `handlers` is, and for the same reason: this is the teardown a
  // sign-out performs, so leaving registrations behind would keep a signed-out session's
  // components attached to whatever socket the next sign-in mints.
  socketEventHandlers.clear();
  socketEventBridges.clear();
}

/**
 * Register a handler for a specific event type. Returns an unsubscribe fn.
 * Multiple subscribers per type are supported.
 */
export function subscribe<T extends Record<string, unknown> = Record<string, unknown>>(
  eventType: string,
  handler: (payload: T, event: AIEvent) => void,
): () => void {
  let set = handlers.get(eventType);
  if (!set) {
    set = new Set();
    handlers.set(eventType, set);
  }
  set.add(handler as RealtimeHandler);

  return () => {
    const current = handlers.get(eventType);
    if (!current) return;
    current.delete(handler as RealtimeHandler);
    if (current.size === 0) handlers.delete(eventType);
  };
}

/**
 * Subscribe to a RAW socket event by name. Returns an unsubscribe fn.
 *
 * The name is whatever the server emits (`dm:item:event`) or whatever socket.io emits
 * locally (`connect`, `disconnect`) — the two are the same channel from a listener's
 * point of view, which is what lets a caller build a connection-state machine and a
 * data feed out of one primitive instead of two.
 *
 * Handlers receive the emit's arguments UNTOUCHED and untyped: `disconnect` hands over a
 * reason string, a server push hands over an envelope. Narrowing is the caller's, at the
 * boundary where the shape is actually known — a `Record<string, unknown>` here would be
 * a claim about payloads this module never inspects.
 *
 * Registering BEFORE the socket exists is supported: the bridge attaches when
 * `getRealtimeSocket` mints one. That ordering is the ordinary case in an effect that
 * registers its listeners and then ensures the connection.
 */
export function onSocketEvent(name: string, handler: SocketEventHandler): () => void {
  let set = socketEventHandlers.get(name);
  if (!set) {
    set = new Set();
    socketEventHandlers.set(name, set);
    const bridge = bridgeFor(name);
    if (socket) socket.on(name, bridge);
  }
  set.add(handler);

  return () => {
    const current = socketEventHandlers.get(name);
    if (!current) return;
    current.delete(handler);
    if (current.size > 0) return;
    socketEventHandlers.delete(name);
    const bridge = socketEventBridges.get(name);
    if (bridge !== undefined) {
      socketEventBridges.delete(name);
      if (socket) socket.off(name, bridge);
    }
  };
}

/**
 * Send on the shared socket. Answers whether there was a socket to send on.
 *
 * NO QUEUEING when there is none, and no socket is created here — this function has no
 * token and minting a connection from a fire-and-forget send would be a side effect its
 * name does not carry. A caller that needs the connection ensures it with
 * `getRealtimeSocket(token)` first, which is what the dm adapter does.
 *
 * The BOOLEAN answers "was there a socket", and NOT "was it connected" — the narrower
 * claim is the one this line can make. A `void` return would let a caller believe it had
 * subscribed; `false` says nothing was even handed to a transport. A socket that EXISTS
 * while disconnected buffers the send and flushes it on reconnect, so `true` there is not
 * a promise that it has left the client. Liveness is {@link isRealtimeConnected}, and the
 * dm adapter reads neither for that: it stays out of `live` until the ack lands.
 */
export function emitRealtime(
  name: string,
  payload: unknown,
  ack?: (response: unknown) => void,
): boolean {
  if (!socket) return false;
  if (ack === undefined) socket.emit(name, payload);
  else socket.emit(name, payload, ack);
  return true;
}

export function isRealtimeConnected(): boolean {
  return !!socket?.connected;
}

export function getActiveSubscriberCount(eventType?: string): number {
  if (eventType) return handlers.get(eventType)?.size ?? 0;
  let total = 0;
  for (const set of handlers.values()) total += set.size;
  return total;
}
